<template>
	<SideNavLayout>
		<section class="analysis-page">
			<p v-if="!store.canRead" class="empty-state" role="alert">{{ t("comparableAnalysis.noAccess") }}</p>
			<SkeletonRows v-else-if="loading" :rows="8" role="status" :aria-label="t('comparableAnalysis.loading')" />
			<template v-else-if="analysis">
				<header class="page-header">
					<RouterLink
						class="back"
						:to="{ name: Route.ComparableAnalyses }"
						:aria-label="t('comparableAnalysis.titlePlural')"
						:title="t('comparableAnalysis.titlePlural')"
					>
						<AppIcon :name="IconName.ArrowLeft" :size="14" :stroke-width="1.75" />
					</RouterLink>
					<div class="title">
						<h1>{{ analysis.name }}</h1>
					</div>
					<div class="actions">
						<template v-if="!editing">
							<Button variant="tertiary" size="xs" shape="pill" :disabled="busy" @click="load">
								<AppIcon :name="IconName.RotateCcw" :size="14" :stroke-width="1.75" class="button-icon" />
								{{ t("comparableAnalysis.reload") }}
							</Button>
							<Button
								v-if="store.canWrite"
								variant="primary"
								size="xs"
								shape="pill"
								:disabled="!!store.draft || busy"
								@click="beginEdit"
							>
								<AppIcon :name="IconName.Pencil" :size="14" :stroke-width="1.75" class="button-icon" />
								{{ t("comparableAnalysis.edit") }}
							</Button>
						</template>
						<template v-else>
							<Button
								class="map-selection"
								type="button"
								variant="secondary"
								size="xs"
								shape="pill"
								:disabled="busy || (input?.comparableInscriptionNumbers.length ?? 0) >= 50"
								@click="selectOnMap"
							>
								{{ t("comparableAnalysis.addFromMap") }}
							</Button>
							<Button type="button" variant="tertiary" size="xs" shape="pill" :disabled="busy" @click="cancelEdit">{{
								t("comparableAnalysis.cancel")
							}}</Button>
							<Button
								variant="primary"
								size="xs"
								shape="pill"
								type="submit"
								form="analysis-edit-form"
								:disabled="busy"
								>{{ t(saving ? "comparableAnalysis.saving" : "comparableAnalysis.save") }}</Button
							>
						</template>
						<Button
							v-if="store.canWrite"
							variant="danger"
							size="xs"
							shape="pill"
							:disabled="busy"
							@click="deleteAnalysis"
						>
							<AppIcon :name="IconName.Trash" :size="14" :stroke-width="1.75" class="button-icon" />
							{{ t(store.deleting ? "comparableAnalysis.deleting" : "comparableAnalysis.delete") }}
						</Button>
					</div>
				</header>
				<p v-if="analysis.missingSubjectParcelIds.length" class="banner" role="status">
					<AppIcon :name="IconName.Info" :size="16" />
					<span>{{ t("comparableAnalysis.missingParcels") }} {{ analysis.missingSubjectParcelIds.join(", ") }}</span>
				</p>
				<p v-if="store.draft && !editing" class="banner" role="status">
					{{ t("comparableAnalysis.pendingDraft") }}
					<RouterLink :to="{ name: Route.Home }">{{ t("comparableAnalysis.continueOnMap") }}</RouterLink>
				</p>
				<form v-if="editing && input" id="analysis-edit-form" class="edit-form" @submit.prevent="save">
					<fieldset :disabled="busy">
						<ComparableAnalysisSettings v-model="store.draft!.input" :disabled="busy">
							<label class="field analysis-name">
								{{ t("comparableAnalysis.name") }}<input v-model="input.name" required maxlength="200" />
							</label>
						</ComparableAnalysisSettings>
					</fieldset>
				</form>
				<p v-if="!analysis.reportDate" class="banner">{{ t("comparableAnalysis.missingReportDate") }}</p>
				<p v-if="error" class="banner error" role="alert">
					<AppIcon :name="IconName.CircleAlert" :size="16" />
					<span>{{ error }}</span>
				</p>
				<p v-if="store.deleting" class="banner" role="status">{{ t("comparableAnalysis.deleting") }}</p>
				<ComparableAnalysisTable
					class="comparison-table"
					v-model:subject-parcels-text="subjectParcelsText"
					:subject="analysis.subject"
					:subject-parcel-area-sqft="analysis.subjectParcelAreaSqft"
					:subject-land-area-override-sqft="input?.subjectLandAreaOverrideSqft"
					@update:subject-land-area-override-sqft="
						value => {
							if (input) input.subjectLandAreaOverrideSqft = value
						}
					"
					:comparables="visibleComparables"
					:analysis-type="input?.analysisType ?? analysis.analysisType"
					:subject-agricultural-inputs="analysis.subjectAgriculturalInputs"
					:subject-agricultural-draft="input?.subjectAgriculturalInputs ?? undefined"
					:comparable-agricultural-draft="input?.comparableAgriculturalInputs ?? undefined"
					@update:subject-agricultural-draft="
						value => {
							if (input) input.subjectAgriculturalInputs = value
						}
					"
					@update:comparable-agricultural-draft="
						value => {
							if (input) input.comparableAgriculturalInputs = value
						}
					"
					:editing="editing"
					:disabled="busy"
					:subject-draft="input?.subject"
					@update:subject-draft="
						value => {
							if (input && value) input.subject = value
						}
					"
					@reorder="
						numbers => {
							if (input) input.comparableInscriptionNumbers = numbers
						}
					"
					@remove="removeComparable"
				/>
			</template>
			<div v-else-if="error" class="empty-state" role="alert">
				<p>{{ error }}</p>
				<Button variant="primary" size="xs" @click="load">{{ t("comparableAnalysis.reload") }}</Button>
			</div>
			<p v-else class="empty-state">{{ t("comparableAnalysis.notFound") }}</p>
		</section>
	</SideNavLayout>
</template>
<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue"
import { useRoute, useRouter, onBeforeRouteLeave, onBeforeRouteUpdate } from "vue-router"
import { useI18n } from "vue-i18n"
import type { ComparableAnalysis } from "@/graphql"
import SideNavLayout from "@/components/layout/SideNavLayout.vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import Button from "@/components/ui/Button.vue"
import SkeletonRows from "@/components/ui/SkeletonRows.vue"
import ComparableAnalysisTable from "@/components/comparable-analyses/ComparableAnalysisTable.vue"
import ComparableAnalysisSettings from "@/components/comparable-analyses/ComparableAnalysisSettings.vue"
import { useComparableAnalysisStore } from "@/stores/comparableAnalysisStore"
import { Route } from "@/utils/routing"
const store = useComparableAnalysisStore(),
	route = useRoute(),
	router = useRouter()
const { t } = useI18n()
const analysis = ref<ComparableAnalysis | null>(null)
const input = computed(() => (store.draft?.target?.id === String(route.params.id) ? store.draft.input : null))
const editing = computed(() => !!input.value)
const saving = computed(() => store.saving)
const busy = computed(() => saving.value || store.deleting)
const loading = ref(false),
	error = ref("")
const subjectParcelsText = computed({
	get: () => store.draft?.subjectParcelsText ?? "",
	set: value => {
		if (editing.value && store.draft) store.draft.subjectParcelsText = value
	},
})
const visibleComparables = computed(() => {
	const saved = analysis.value?.comparables ?? []
	if (!editing.value || !input.value) return saved
	const byNumber = new Map(saved.map(comparable => [comparable.inscriptionNumber, comparable]))
	return input.value.comparableInscriptionNumbers.map(
		inscriptionNumber =>
			byNumber.get(inscriptionNumber) ?? {
				inscriptionNumber,
				agriculturalInputs: {},
				sourceInscriptionNumber: store.draft?.summaries?.[inscriptionNumber]?.sourceInscriptionNumber,
				data: store.draft?.summaries?.[inscriptionNumber] ?? null,
			}
	)
})
let revision = 0
async function load() {
	const request = ++revision
	analysis.value = null
	error.value = ""
	if (!store.canRead) return
	loading.value = true
	try {
		const result = await store.get(String(route.params.id))
		if (request === revision) {
			if (result) store.restoreDraftAssumptions(result)
			analysis.value = result
		}
	} catch (failure) {
		if (request === revision) error.value = failure instanceof Error ? failure.message : t("comparableAnalysis.error")
	} finally {
		if (request === revision) loading.value = false
	}
}
function beginEdit() {
	if (analysis.value) store.startEditing(analysis.value)
	error.value = ""
}
function cancelEdit() {
	if (editing.value) store.cancel()
	error.value = ""
}
function removeComparable(number: string) {
	if (!input.value || busy.value) return
	input.value.comparableInscriptionNumbers = input.value.comparableInscriptionNumbers.filter(value => value !== number)
}
async function selectOnMap() {
	if (!editing.value || busy.value) return
	store.prepareMapSelection()
	await router.push({ name: Route.Home })
}
async function save() {
	if (!analysis.value || !input.value || busy.value) return
	const request = revision
	error.value = ""
	try {
		const saved = await store.saveDraft()
		if (request !== revision || !saved) return
		analysis.value = saved
		cancelEdit()
	} catch (failure) {
		if (request === revision) error.value = failure instanceof Error ? failure.message : t("comparableAnalysis.error")
	}
}
async function deleteAnalysis() {
	if (!analysis.value || !store.canWrite || busy.value) return
	if (!window.confirm(t("comparableAnalysis.confirmDelete", { name: analysis.value.name }))) return
	const request = revision
	error.value = ""
	try {
		if (!(await store.remove(analysis.value.id)) || request !== revision) return
		analysis.value = null
		await router.replace({ name: Route.ComparableAnalyses })
	} catch (failure) {
		if (request === revision)
			error.value = failure instanceof Error ? failure.message : t("comparableAnalysis.deleteError")
	}
}
onUnmounted(() => revision++)
function confirmLeave() {
	if (!editing.value) return true
	if (saving.value || !window.confirm(t("comparableAnalysis.discard"))) return false
	cancelEdit()
	return true
}
onBeforeRouteLeave(to => (!store.deleting || !store.canWrite) && (to.name === Route.Home || confirmLeave()))
onBeforeRouteUpdate(
	to => (!store.deleting || !store.canWrite) && (String(to.params.id) === String(route.params.id) || confirmLeave())
)
watch(
	() => [route.params.id, store.canRead],
	() => {
		void load()
	},
	{ immediate: true }
)
watch(
	() => store.canWrite,
	value => {
		if (!value) cancelEdit()
	}
)
</script>
<style scoped>
.analysis-page {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);
	height: 100%;
	box-sizing: border-box;
	padding: var(--spacing-xl);
	min-width: 0;
	font-family: var(--font-family-primary);
	> * {
		flex-shrink: 0;
	}
	.comparison-table {
		flex: 1;
		min-height: 0;
	}
	.button-icon {
		margin-right: var(--spacing-xs);
	}
	.page-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: var(--spacing-md);
		.back {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: var(--size-button-s);
			height: var(--size-button-s);
			border-radius: var(--border-radius-base);
			color: var(--neutral-600);
			&:hover {
				background: var(--neutral-100);
				color: var(--neutral-900);
			}
		}
		.title {
			flex: 1;
			min-width: 0;
		}
		h1 {
			margin: 0;
			font-size: var(--font-size-xl);
			font-weight: var(--font-weight-semibold);
			color: var(--neutral-900);
		}
		.actions {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: var(--spacing-sm);
		}
	}
	.banner {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		margin: 0;
		padding: var(--spacing-sm) var(--spacing-md);
		border-radius: var(--border-radius-md);
		background: var(--accent-info-background);
		font-size: var(--font-size-small);
		color: var(--accent-info);
		&.error {
			background: color-mix(in srgb, var(--accent-warning) 10%, transparent);
			color: var(--accent-warning);
		}
	}
	.edit-form {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
	}
	.edit-form fieldset {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
		margin: 0;
		padding: var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-md);
	}
	.analysis-name {
		flex: 2 1 260px;
	}

	.field {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		min-width: 0;
		font-size: var(--font-size-xs);
		color: var(--neutral-600);
		label {
			font: inherit;
			color: inherit;
		}
	}
	input {
		flex: none;
		min-width: 0;
		height: var(--size-button-s);
		box-sizing: border-box;
		padding: 0 var(--spacing-sm);
		border: var(--border-width-thin) solid var(--neutral-400);
		border-radius: var(--border-radius-base);
		background: var(--neutral-0);
		font: inherit;
		font-size: var(--font-size-small);
		color: var(--neutral-900);
		outline: none;
		&:focus {
			border-color: var(--neutral-900);
		}
	}
	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--spacing-sm);
		margin: 0;
		padding: var(--spacing-2xl);
		text-align: center;
		color: var(--neutral-600);
		p {
			margin: 0;
		}
	}
	@media (max-width: 768px) {
		padding: var(--spacing-md);
	}
}
</style>
